import React, { useState, useEffect } from "react";
import Modal from "../../../../components/Modal";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "../../../../components/ui/input-otp";
import { toast } from "react-toastify";
import {
  sendAadharOtpAction,
  verifyAadharOtpAction,
  updateAadharEncryptedAction,
  getDocumentNameForField,
} from "../../../../actions/aadharOtpActions";
import "./index.scss";

/**
 * OtpVerificationModal
 *
 * mode="view"  → verify OTP to VIEW the existing Aadhar (eye icon flow)
 * mode="edit"  → enter new Aadhar + verify OTP to UPDATE it (edit icon flow)
 */
const OtpVerificationModal = ({
  isOpen,
  onClose,
  adminEmail = "",
  fieldKey = "",
  entityPrimaryId = 0,
  // view mode
  mode = "view",
  onVerifySuccess,
  fieldName = "Aadhar Number",
  // edit mode
  onSuccess,
  fieldLabel,
  existingValue = "",
}) => {
  const isEdit = mode === "edit";
  const label = fieldLabel || fieldName;

  const [step, setStep] = useState("form"); // "form" | "otp"
  const [newAadhar, setNewAadhar] = useState("");
  const [newAadharError, setNewAadharError] = useState("");
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState(""); // inline error under OTP slots
  const [isSending, setIsSending] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [timer, setTimer] = useState(0);

  useEffect(() => {
    if (timer > 0) {
      const id = setInterval(() => setTimer((p) => p - 1), 1000);
      return () => clearInterval(id);
    }
  }, [timer]);

  useEffect(() => {
    if (!isOpen) {
      setStep("form");
      setNewAadhar("");
      setNewAadharError("");
      setOtp("");
      setOtpError("");
      setTimer(0);
    }
  }, [isOpen]);

  const formatTimer = () =>
    `${String(Math.floor(timer / 60)).padStart(2, "0")}:${String(timer % 60).padStart(2, "0")}`;

  const validateAadhar = (val) => {
    const c = val.replace(/\s/g, "");
    if (!c) return "Aadhar number is required";
    if (!/^\d+$/.test(c)) return "Only digits are allowed";
    if (c.length !== 12) return "Must be exactly 12 digits";
    return "";
  };

  const handleSendOtp = async () => {
    if (isEdit) {
      const err = validateAadhar(newAadhar);
      if (err) {
        setNewAadharError(err);
        return;
      }
      setNewAadharError("");
    }
    setIsSending(true);
    try {
      const res = await sendAadharOtpAction(adminEmail, isEdit);
      if (res.success) {
        setStep("otp");
        setTimer(15 * 60);
        toast.success(`OTP sent to ${adminEmail}`);
      } else {
        toast.error(res.message || "Failed to send OTP");
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleSubmit = async () => {
    if (otp.length !== 6) {
      setOtpError("Please enter all 6 digits");
      return;
    }
    setOtpError("");
    setIsSubmitting(true);
    try {
      const documentName = getDocumentNameForField(fieldKey);
      if (isEdit) {
        const res = await updateAadharEncryptedAction({
          entityPrimaryId,
          documentName,
          encryptedData: newAadhar.replace(/\s/g, ""),
          email: adminEmail,
          otp,
        });
        if (res.success) {
          toast.success(res.message || "Aadhar updated successfully!");
          onSuccess?.(newAadhar.replace(/\s/g, ""));
          onClose();
        } else {
          // Keep modal open — show inline error, red OTP slots, clear OTP
          setOtpError(res.message || "Invalid OTP. Please try again.");
          setOtp("");
        }
      } else {
        const res = await verifyAadharOtpAction({
          entityPrimaryId,
          documentName,
          email: adminEmail,
          otp,
        });
        console.log("Resposne  2 : ", res.originalValue);
        if (res.success) {
          toast.success(res.message || "Verified successfully!");
          onVerifySuccess?.(res.originalValue);
          onClose();
        } else {
          setOtpError(res.message || "Invalid OTP. Please try again.");
          setOtp("");
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = () => {
    if (timer > 0) return;
    setOtp("");
    setStep("form");
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? `Update ${label}` : `Verify to View ${label}`}
      size="medium"
    >
      <div className="ovm otp-modal-body">
        {/* ── Icon + description ─────────────────────────────────────── */}
        <div className={`ovm__hero ovm__hero--${isEdit ? "edit" : "view"}`}>
          <div className="ovm__hero-icon">{isEdit ? "✏️" : "🔒"}</div>
          <p className="ovm__hero-text">
            {isEdit
              ? `Enter the new ${label} below, then verify with OTP sent to your email.`
              : `An OTP will be sent to your email to securely view the ${label}.`}
          </p>
        </div>

        {/* ── STEP 1: form ──────────────────────────────────────────── */}
        {step === "form" && (
          <div className="ovm__step">
            {/* Edit mode: show current + new input */}
            {isEdit && (
              <>
                <div className="ovm__current">
                  <span className="ovm__current-label">Current {label}</span>
                  <span className="ovm__current-value">
                    {existingValue || "—"}
                  </span>
                </div>

                <div className="ovm__field">
                  <label className="ovm__field-label">
                    New {label}
                    <span className="ovm__req"> *</span>
                  </label>
                  <div className="ovm__field-wrap">
                    <input
                      type="text"
                      className={`ovm__input${newAadharError ? " ovm__input--err" : ""}`}
                      value={newAadhar}
                      onChange={(e) => {
                        const v = e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 12);
                        setNewAadhar(v);
                        if (newAadharError) setNewAadharError("");
                      }}
                      placeholder="Enter 12-digit Aadhar number"
                      maxLength={12}
                    />
                    <span
                      className={`ovm__char-count${newAadhar.length === 12 ? " ovm__char-count--done" : ""}`}
                    >
                      {newAadhar.length}/12
                    </span>
                  </div>
                  {newAadharError && (
                    <p className="ovm__err">{newAadharError}</p>
                  )}
                </div>
              </>
            )}

            {/* Email info */}
            <div className="ovm__email-info">
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
              <span>
                OTP will be sent to <strong>{adminEmail}</strong>
              </span>
            </div>

            <button
              type="button"
              className="ovm__btn ovm__btn--primary"
              onClick={handleSendOtp}
              disabled={isSending || (isEdit && !newAadhar)}
            >
              {isSending ? (
                <>
                  <span className="ovm__spin" /> Sending...
                </>
              ) : (
                "Send OTP"
              )}
            </button>
          </div>
        )}

        {/* ── STEP 2: OTP entry ─────────────────────────────────────── */}
        {step === "otp" && (
          <div className="ovm__step">
            {/* Summary of what's being updated */}
            {isEdit && (
              <div className="ovm__summary">
                <div className="ovm__summary-row">
                  <span>Current</span>
                  <span className="ovm__mono">{existingValue || "—"}</span>
                </div>
                <div className="ovm__summary-arrow">↓</div>
                <div className="ovm__summary-row ovm__summary-row--new">
                  <span>New</span>
                  <span className="ovm__mono">{newAadhar}</span>
                </div>
              </div>
            )}

            <p className="ovm__otp-label">Enter the 6-digit OTP</p>

            <div
              className={`ovm__otp-slots${otpError ? " ovm__otp-slots--error" : ""}`}
            >
              <InputOTP
                maxLength={6}
                value={otp}
                onChange={(v) => {
                  setOtp(v);
                  if (otpError) setOtpError("");
                }}
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                </InputOTPGroup>
                <InputOTPGroup>
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>

            {/* Inline OTP error */}
            {otpError && (
              <div className="ovm__otp-error">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                {otpError}
              </div>
            )}

            <button
              type="button"
              className="ovm__btn ovm__btn--success"
              onClick={handleSubmit}
              disabled={isSubmitting || otp.length !== 6}
            >
              {isSubmitting ? (
                <>
                  <span className="ovm__spin" />{" "}
                  {isEdit ? "Updating..." : "Verifying..."}
                </>
              ) : isEdit ? (
                "Verify & Update"
              ) : (
                "Verify OTP"
              )}
            </button>

            <div className="ovm__resend">
              {timer > 0 ? (
                <p className="ovm__resend-timer">
                  Resend available in <strong>{formatTimer()}</strong>
                </p>
              ) : (
                <button
                  type="button"
                  className="ovm__btn ovm__btn--ghost"
                  onClick={handleResend}
                >
                  ↩ Resend OTP
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default OtpVerificationModal;
